import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "oneLarge",
    "twoMedium",
    "fourSmall",
    "printForm",
    "preview",
    "previewCover",
    "patternList",
    "patternEditor",
    "printButton",
    "downloadButton",
    "lockOverlay",
    "step1",
    "step2",
    "backToStep1Button"
  ]

  // Add constants
  static values = {
    minFontSize: { type: Number, default: 8 },
    maxFontSize: { type: Number, default: 136 },
    stepSize: { type: Number, default: 1 }
  }

  connect() {
    this.positionsCollection = []
    this.printButtonTimeout = null
  }

  setCardsPerPage(event) {
    const cardsPerPage = parseInt(event.target.dataset.value, 10)
    const targetMap = {
      1: [this.oneLargeTarget, this.twoMediumTarget, this.fourSmallTarget],
      2: [this.twoMediumTarget, this.oneLargeTarget, this.fourSmallTarget],
      4: [this.fourSmallTarget, this.oneLargeTarget, this.twoMediumTarget]
    }

    if (targetMap[cardsPerPage]) {
      const [showTarget, ...hideTargets] = targetMap[cardsPerPage]
      showTarget.classList.remove("is-hidden")
      hideTargets.forEach(target => target.classList.add("is-hidden"))
    }

    this.previewCoverTarget.classList.remove("card-preview__cover--hidden")
    this.debounce(() => {
      // Add delay before hiding the preview cover
      setTimeout(() => {
        this.previewCoverTarget.classList.add("card-preview__cover--hidden")
      }, 2000) // 2 seconds delay
    }, 100)
    
    this.setActiveButton(event.target)
    this.updatePrintFormValue('cards_per_page', cardsPerPage)
    this.resetPrintButton()
  }

  setNumberOfCards(event) {
    const numberOfCards = parseInt(event.target.dataset.value, 10)

    this.setActiveButton(event.target)
    this.updatePrintFormValue('number_of_cards', numberOfCards)
    this.resetPrintButton()
  }

  setPattern(event) {
    const patternPositions = event.target.dataset.patternPositions

    // Remove selected class from all pattern-list-item elements
    this.patternListTarget.querySelectorAll(".pattern-list-item").forEach(patternListItem => {
      patternListItem.classList.remove("pattern-list-item--is-selected")
    })

    // Add selected class to the target
    if (!event.target.classList.contains("pattern-list-item--is-selected")) {
      event.target.classList.add("pattern-list-item--is-selected")
    }

    this.updatePrintFormValue('positions', patternPositions)
    this.debounce(() => this.updatePreview(patternPositions), 100)
    this.resetPrintButton()
  }

  togglePatternEditor(event) {
    event.preventDefault()
    this.patternEditorTarget.classList.toggle("is-hidden")  
    this.patternListTarget.classList.toggle("is-hidden")
    this.resetPrintButton()
  }

  setPosition(event) {
    const target = event.target
    const position = target.dataset.position

    if (this.positionsCollection.includes(position)) {
      target.classList.remove("pattern-marked")
      this.positionsCollection = this.positionsCollection.filter(coord => coord !== position)
    } else {
      target.classList.add("pattern-marked")
      this.positionsCollection.push(position)
    }

    const positions = this.positionsCollection.join(",")
    this.updatePrintFormValue('positions', positions)
    this.debounce(() => this.updatePreview(positions), 100)
    this.resetPrintButton()
  }

  printCards(event) {
    event.preventDefault()

    // Disable the print button
    this.printButtonTarget.disabled = true

    // Show the lock overlay
    this.lockOverlayTarget.classList.remove("is-hidden")

    // Change the text to "Please wait..."
    this.printButtonTarget.textContent = "Please wait..."

    // Wait for 5 seconds
    this.printButtonTimeout = setTimeout(() => {
      this.step1Target.classList.add("is-hidden")
      this.step2Target.classList.remove("is-hidden")
  
      // Hide the lock overlay
      this.lockOverlayTarget.classList.add("is-hidden")
    }, 5000)
  }

  backToStep1(event) {
    event.preventDefault()

    this.step1Target.classList.remove("is-hidden")
    this.step2Target.classList.add("is-hidden")
    this.resetPrintButton()
  }

  // Private methods
  // ---------------------------------------------------------------------------
  resetPrintButton() {
    this.printButtonTarget.disabled = false
    this.printButtonTarget.textContent = "Print my free bingo cards"
    this.printButtonTarget.classList.remove("is-hidden")

    // Clear the timeout
    if (this.printButtonTimeout) {
      clearTimeout(this.printButtonTimeout)
      this.printButtonTimeout = null
    }
  }

  setActiveButton(button) {
    const siblings = button.closest(".buttons").querySelectorAll(".button")
    siblings.forEach(sibling => sibling.classList.remove("is-dark"))
    button.classList.add("is-dark")
  }

  updatePrintFormValue(fieldName, value) {
    this.printFormTarget[fieldName].value = value
  }

  updatePreview(patternPositions) {
    const cells = this.previewTarget.querySelectorAll(".classic-card__cell-text--number")
    const patternPositionsArray = patternPositions.split(",")

    // Remove all pattern-marker elements first
    cells.forEach(cell => {
      cell.classList.remove("marked")
    })

    // Add new pattern-marker elements
    cells.forEach(cell => {
      if (patternPositionsArray.includes(cell.dataset.position)) {
        cell.classList.add("marked")
      }
    })
  }

  // Add debounce helper
  debounce(func, wait) {
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(func, wait)
  }

  // Optimize resizeToFit with binary search if needed
  resizeToFit(element) {
    if (!element) return;
    
    const parent = element.parentElement
    let min = this.minFontSizeValue
    let max = this.maxFontSizeValue
    
    while (min <= max) {
      const mid = Math.floor((min + max) / 2)
      element.style.fontSize = `${mid}px`
      
      if (element.scrollHeight <= parent.clientHeight && 
          element.scrollWidth <= parent.clientWidth) {
        min = mid + 1
      } else {
        max = mid - 1
      }
    }
    
    element.style.fontSize = `${max}px`
  }
}
